// apps/provider/src/services/api/priorAuth.js
// Prior authorization CRUD, summary, and alerts methods
//
// Methods: getPriorAuthorizations, getPriorAuthorization, createPriorAuthorization,
//          updatePriorAuthorization, deletePriorAuthorization,
//          getPriorAuthorizationSummary, getPriorAuthorizationAlerts,
//          getPriorAuthorizationAlertsBatch, getPriorAuthorizationUtilization
//
// Response types: @prism-ehr/shared → PriorAuthorizationResponse, PriorAuthorizationListResponse,
//                  PriorAuthorizationSummary, PriorAuthorizationAlert

/**
 * @returns {Object} Prior authorization domain methods (bound to `this` of the composed apiService)
 */
export function createPriorAuthMethods() {
  return {
    /**
     * List prior authorizations with filters.
     *
     * Xano: GET /prior-authorization
     * Xano file: prior-authorization_GET.xs
     *
     * @param {Object} [filters]
     * @param {string} [filters.patient_id]
     * @param {number} [filters.case_id] - Auths linked to this case (primary case_id or any entry of case_ids)
     * @param {string} [filters.status]
     * @param {string} [filters.statuses] - CSV multi-status filter, e.g. "denied,appealed"
     * @param {boolean} [filters.effective] - With statuses: lifecycle-aware bucketing. Active statuses past expiration_date match "expired" instead of their stored status (derived, no writes). Rows carry _effective_status either way.
     * @param {string} [filters.auth_type]
     * @param {string} [filters.requesting_provider_id]
     * @param {string} [filters.provider_id] - Case therapist (case.provider_id of the auth's primary case) filter, CSV
     * @param {string} [filters.query] - Server-side search (patient name, ref #, payer)
     * @param {Object} [filters.external] - Sort override, e.g. {sortBy: "prior_authorization.expiration_date", orderBy: "asc"}. Also accepts computed "visits_remaining"; unknown limits sort last in either direction. Ignored while query is set.
     * @param {boolean} [filters.group_by_case] - One row per case: the representative (newest non-terminal effective status, else newest) with the other matching auths on that case under `_case_siblings`. itemsTotal counts cases (except while query is set). Admin dashboard only; every other caller needs the flat list.
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @returns {Promise<import('@prism-ehr/shared').PriorAuthorizationListResponse>}
     */
    async getPriorAuthorizations(filters = {}) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, key === 'external' ? JSON.stringify(value) : value);
        }
      });
      const query = params.toString();
      return this.request(`/prior-authorization${query ? `?${query}` : ''}`, {
        method: 'GET'
      }, 'getPriorAuthorizations');
    },

    /**
     * Prior authorization coverage queue: one row per ACTIVE case with any non-cancelled
     * auth, ordered by soonest loss of coverage. Each item is the case's drawn-from auth
     * (full record + patient/case addons) with `_case_siblings` (the case's other auths),
     * `_visits_used_auto`, `_effective_status`, `_next_appointment_date`, and
     * `_queue: {covered, pending_request, runway_visits, days_left, expires_at}`.
     * Computed per org and cached 300 s behind the admission gate; wrap calls in
     * `retryOnBusy` (429 = the org's queue is being recomputed for another request).
     *
     * Xano: GET /prior-authorization-queue
     * Xano file: prior-authorization-queue_GET.xs
     *
     * @param {Object} [params]
     * @param {number} [params.page]
     * @param {number} [params.limit] - max 100
     * @param {string} [params.query] - patient name, reference #, or payer substring
     * @param {string} [params.provider_id] - CSV of case therapist user ids
     * @param {boolean} [params.nocache] - recompute the org's queue (after a write)
     * @returns {Promise<import('@prism-ehr/shared').PriorAuthorizationQueueResponse>}
     */
    async getPriorAuthorizationQueue(params = {}) {
      const search = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '' && value !== false) {
          search.append(key, value);
        }
      });
      const query = search.toString();
      return this.request(`/prior-authorization-queue${query ? `?${query}` : ''}`, {
        method: 'GET'
      }, 'getPriorAuthorizationQueue');
    },

    /**
     * Get a single prior authorization by ID.
     *
     * Xano: GET /prior-authorization/{prior_auth_id}
     * Xano file: prior-authorization_{prior_auth_id}_GET.xs
     *
     * @param {number} id
     * @returns {Promise<import('@prism-ehr/shared').PriorAuthorizationResponse>}
     */
    async getPriorAuthorization(id) {
      return this.request(`/prior-authorization/${id}`, {
        method: 'GET'
      }, 'getPriorAuthorization');
    },

    /**
     * Create a new prior authorization.
     *
     * Xano: POST /prior-authorization
     * Xano file: prior-authorization_POST.xs
     *
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').PriorAuthorizationResponse>}
     */
    async createPriorAuthorization(data) {
      return this.request('/prior-authorization', {
        method: 'POST',
        body: JSON.stringify(data)
      }, 'createPriorAuthorization');
    },

    /**
     * Update a prior authorization.
     *
     * Xano: PATCH /prior-authorization/{prior_auth_id}
     * Xano file: prior-authorization_{prior_auth_id}_PATCH.xs
     *
     * @param {number} id
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').PriorAuthorizationResponse>}
     */
    async updatePriorAuthorization(id, data) {
      return this.request(`/prior-authorization/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }, 'updatePriorAuthorization');
    },

    /**
     * Delete a prior authorization.
     *
     * Xano: DELETE /prior-authorization/{prior_auth_id}
     * Xano file: prior-authorization_{prior_auth_id}_DELETE.xs
     *
     * @param {number} id
     * @returns {Promise<void>}
     */
    async deletePriorAuthorization(id) {
      return this.request(`/prior-authorization/${id}`, {
        method: 'DELETE'
      }, 'deletePriorAuthorization');
    },

    /**
     * Get org-wide prior authorization summary counts.
     *
     * Xano: GET /prior-authorization-summary
     * Xano file: prior-authorization_summary_GET.xs
     *
     * @returns {Promise<import('@prism-ehr/shared').PriorAuthorizationSummary>}
     */
    async getPriorAuthorizationSummary() {
      return this.request('/prior-authorization-summary', {
        method: 'GET'
      }, 'getPriorAuthorizationSummary');
    },

    /**
     * Get prior authorization alerts for a patient.
     *
     * Xano: GET /prior-authorization-alerts
     * Xano file: prior-authorization_alerts_GET.xs
     *
     * @param {Object} [filters]
     * @param {string} [filters.patient_id]
     * @returns {Promise<import('@prism-ehr/shared').PriorAuthorizationResponse[]>}
     */
    async getPriorAuthorizationAlerts(filters = {}) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });
      const query = params.toString();
      return this.request(`/prior-authorization-alerts${query ? `?${query}` : ''}`, {
        method: 'GET'
      }, 'getPriorAuthorizationAlerts');
    },

    /**
     * Batch fetch prior auth alerts for multiple patients with visit attribution.
     *
     * Xano: POST /prior-authorization-alerts-batch (GET twin exists but has no caller; the pair is kept byte-identical)
     * Xano file: prior-authorization-alerts-batch_POST.xs
     *
     * @param {string[]} patientIds
     * @returns {Promise<import('@prism-ehr/shared').PriorAuthorizationResponse[]>}
     */
    async getPriorAuthorizationAlertsBatch(patientIds) {
      return this.request('/prior-authorization-alerts-batch', {
        method: 'POST',
        body: JSON.stringify({ patient_ids: patientIds.join(',') }),
      }, 'getPriorAuthorizationAlertsBatch');
    },

    /**
     * Org-wide utilization rollup across active authorizations: active count,
     * total visits remaining, near-exhaustion count (3 or fewer visits left),
     * and per-auth detail rows. Redis-cached 300 s server-side and behind the
     * reporting admission gate, so it can answer HTTP 429; callers must wrap
     * it in `retryOnBusy` (apps/provider/src/utils/retryOnBusy.js).
     *
     * Xano: GET /prior-authorization-utilization
     * Xano file: prior-authorization-utilization_GET.xs
     *
     * @param {Object} [options]
     * @param {boolean} [options.nocache] - Bypass the server-side cache (Refresh)
     * @returns {Promise<{ active_count: number, visits_remaining: number, near_exhaustion: number, details: Object[], total_count: number, computed_at: string }>}
     */
    async getPriorAuthorizationUtilization({ nocache } = {}) {
      return this.request(`/prior-authorization-utilization${nocache ? '?nocache=1' : ''}`, {
        method: 'GET'
      }, 'getPriorAuthorizationUtilization');
    },
  };
}
